import React, { useEffect, useRef } from 'react';
import PerspectiveCanvas from './PerspectiveCanvas'; 
import gpuClusterImage from '../assets/gpu-cluster.webp';
import gpuSelectionImage from '../assets/gpu-selection.webp';
import regionSelectionImage from '../assets/region-selection.webp';
import costEfficiencyImage from '../assets/cost-efficiency.webp';
import '../css/Network.css'; 

const AIDePINNetwork = () => {
  const featureRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
          }
        });
      },
      { threshold: 0.1 }
    );

    featureRefs.current.forEach((ref) => {
      if (ref) {
        observer.observe(ref);
      }
    });

    return () => {
      featureRefs.current.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  return (
    <section id="ai-depin-network" className="network-section">
      <h2>AI-DePIN Network: Build Your AI GPU Cluster</h2>

      <div className="network-features">
        {/* Ligne 1 - Feature 1 et Feature 2 */}
        <div className="feature-network-row">
          <div className="feature-network-item slide-left" ref={(el) => (featureRefs.current[0] = el)}>
            <PerspectiveCanvas />
            <div className="feature-network-icons">
              <img src={gpuClusterImage} alt="GPU Cluster" className="feature-network-image" />
              <h3>Custom GPU Clusters</h3>
            </div>
            <div className="feature-network-content">
              <p>Build scalable and powerful GPU clusters tailored to your AI needs. Whether your business requires advanced GPUs for deep learning or lighter processing tasks, AI-DePIN offers the flexibility to construct clusters that match your exact requirements.</p>
              <p>This customization ensures that startups and enterprises can access the precise computing power they need to deploy AI solutions efficiently.</p>
            </div>
          </div>

          <div className="feature-network-item slide-right reverse" ref={(el) => (featureRefs.current[1] = el)}>
            <PerspectiveCanvas />
            <div className="feature-network-icons">
              <h3>Wide Selection of GPU Models</h3>
              <img src={gpuSelectionImage} alt="GPU Selection" className="feature-network-image" />
            </div>
            <div className="feature-network-content">
              <p>AI-DePIN provides access to a broad range of GPU models, including NVIDIA, AMD, and AI-specialized graphics cards.</p>
              <p>This selection allows businesses to choose from a variety of options, ranging from high-end GPUs for resource-intensive tasks to more cost-effective models. This makes the platform versatile for various industries and workloads..</p>
            </div>
          </div>
        </div>

        {/* Ligne 2 - Feature 3 et Feature 4 */}
        <div className="feature-network-row">
          <div className="feature-network-item slide-left" ref={(el) => (featureRefs.current[2] = el)}>
            <PerspectiveCanvas />
            <div className="feature-network-icons">
              <img src={regionSelectionImage} alt="Region Selection" className="feature-network-image" />
              <h3>Regional and Connectivity Selection</h3>
            </div>
            <div className="feature-network-content">
              <p>Optimize your AI workloads by selecting GPU clusters based on regional availability and connectivity preferences.</p>
              <p>AI-DePIN enables businesses to deploy clusters in regions that offer the best latency and network performance, ensuring efficient processing and seamless operations for AI-driven projects. This feature reduces processing delays and boosts overall network efficiency.</p>
            </div>
          </div>

          <div className="feature-network-item slide-right reverse" ref={(el) => (featureRefs.current[3] = el)}>
            <PerspectiveCanvas />
            <div className="feature-network-icons">
              <h3>Optimized Cost Efficiency</h3>
              <img src={costEfficiencyImage} alt="Cost Efficiency" className="feature-network-image" />
            </div>
            <div className="feature-network-content">
              <p>By leveraging a decentralized infrastructure, AI-DePIN significantly reduces operating costs compared to traditional hosting services.</p>
              <p>This cost efficiency allows businesses to deploy AI solutions while minimizing expenses, ensuring that startups and enterprises alike can scale their operations without compromising on performance or security.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AIDePINNetwork;
